import React, { useState, useEffect, useRef } from 'react';
import { showcaseApi, getMediaUrl } from '../services/api';

const DEFAULT_SLIDES = [
  {
    _id: 'default-1',
    tag: 'Mental Health Practitioners',
    tagIcon: 'fas fa-user-md',
    title: 'Meet Professionals Who Care',
    subtitle: 'Explore support across specialisations',
    description:
      'Connect with verified psychologists, psychiatrists, counsellors, and therapists across diverse fields and specialisations to find the exact care that fits your needs.',
    btnText: 'Explore Practitioners',
    btnLink: '/therapists',
    image: 'assets/images/1/Mental Health.png',
    alt: 'Mental Health Practitioners',
  },
  {
    _id: 'default-2',
    tag: 'Guided Audio Care',
    tagIcon: 'fas fa-headphones',
    title: 'Calm Your Mind Through Guided Audios',
    subtitle: 'Available in 2 languages and voices',
    description:
      'Listen to soothing meditations, mindfulness guides, and calming audio sessions carefully curated to help you de-stress, focus, and find inner peace anytime.',
    btnText: 'Listen to Audios',
    btnLink: '/self-help',
    image: 'assets/images/1/Mental Health Resources.png',
    alt: 'Guided Audios',
  },
  {
    _id: 'default-3',
    tag: 'Supportive Community',
    tagIcon: 'fas fa-users',
    title: 'Community Sharing Wall',
    subtitle: 'Share your thoughts and emotions with like-minded people',
    description:
      'Express yourself freely in a warm, moderated, and judgment-free space. Be part of a supportive community where lived experiences and feelings are heard and validated.',
    btnText: 'Visit Sharing Wall',
    btnLink: '/community',
    image: 'assets/images/1/Sharing Wall.png',
    alt: 'Community Sharing Wall',
  },
  {
    _id: 'default-4',
    tag: 'For Practitioners',
    tagIcon: 'fas fa-chart-line',
    title: 'Grow Your Practice',
    subtitle: 'Scale without commissions or restrictions',
    description:
      'If you are looking for a commission-free space to scale your private practice with full autonomy, built-in practice management tools, and enhanced visibility, we might be a perfect match.',
    btnText: 'Join As A Professional',
    btnLink: '/mhp-guide',
    image: 'assets/images/IMG_9300 (1).PNG',
    alt: 'Grow Your Practice',
  },
];

const DEFAULT_SETTINGS = {
  sectionTitle: 'Explore PsychMitra',
  bgImage: 'assets/images/backgrounds/why-choose-two-bg.png',
  autoPlayInterval: 5500,
  isEnabled: true,
};

function AboutSliderSection() {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const touchStartX = useRef(null);

  // Fetch dynamic showcase slides & settings from backend
  useEffect(() => {
    let isMounted = true;
    const loadShowcaseData = async () => {
      try {
        const res = await showcaseApi.getPublicShowcase();
        if (isMounted && res?.success && res.data) {
          if (Array.isArray(res.data.slides) && res.data.slides.length > 0) {
            setSlides(res.data.slides);
          }
          if (res.data.settings) {
            setSettings(res.data.settings);
          }
        }
      } catch (err) {
        console.debug('Loaded default fallback showcase slides:', err);
      }
    };

    loadShowcaseData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Intersection Observer for scroll entrance
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-slide effect based on settings
  useEffect(() => {
    if (isPaused || slides.length <= 1 || settings?.isEnabled === false) return;
    const intervalMs = settings?.autoPlayInterval || 5500;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(interval);
  }, [isPaused, slides.length, settings?.autoPlayInterval, settings?.isEnabled]);

  // Clamp currentSlide if slides array size changes
  useEffect(() => {
    if (currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  const handlePrev = () => {
    if (slides.length <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    if (slides.length <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || slides.length <= 1) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 50) {
      handleNext();
    } else if (diff < -50) {
      handlePrev();
    }
    touchStartX.current = null;
  };

  if (settings?.isEnabled === false || slides.length === 0) {
    return null;
  }

  const active = slides[currentSlide] || slides[0];

  const getSlideImageUrl = (img) => {
    if (!img) return 'assets/images/1/Mental Health.png';
    return getMediaUrl(img);
  };

  const getBgImageUrl = () => {
    if (!settings?.bgImage) return 'assets/images/backgrounds/why-choose-two-bg.png';
    return getMediaUrl(settings.bgImage);
  };

  return (
    <>
      <style>
        {`
          .about-slider-section {
            position: relative;
            padding: 80px 0 70px;
            background-color: #f7f9f6;
            background-size: cover;
            background-position: center center;
            background-repeat: no-repeat;
            overflow: hidden;
          }

          .about-slider-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, rgba(247, 249, 246, 0.94) 0%, rgba(255, 255, 255, 0.88) 100%);
            z-index: 1;
          }

          .about-slider-section .container {
            position: relative;
            z-index: 2;
          }

          .about-slider__nav-tabs {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 12px;
            margin-bottom: 40px;
          }

          .about-slider__tab-btn {
            background: #ffffff;
            border: 1px solid #e1e7e0;
            color: #3b493e;
            padding: 10px 22px;
            border-radius: 30px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          }

          .about-slider__tab-btn:hover {
            border-color: #5b9655;
            color: #5b9655;
            transform: translateY(-2px);
          }

          .about-slider__tab-btn.active {
            background: #5b9655;
            border-color: #5b9655;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(91, 150, 85, 0.35);
          }

          .about-slider__card {
            background: #ffffff;
            border-radius: 24px;
            box-shadow: 0 16px 40px rgba(28, 48, 30, 0.08);
            border: 1px solid #e9eee8;
            padding: 40px;
            min-height: 480px;
            display: flex;
            align-items: center;
            transition: all 0.4s ease;
            position: relative;
          }

          .about-slider__img-wrap {
            position: relative;
            width: 100%;
            height: 380px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(145deg, #f0f6ee 0%, #e3ede0 100%);
            border-radius: 20px;
            padding: 24px;
            overflow: hidden;
            box-shadow: inset 0 2px 8px rgba(0,0,0,0.02);
          }

          .about-slider__img-wrap img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            border-radius: 12px;
            transition: transform 0.5s ease;
          }

          .about-slider__img-wrap:hover img {
            transform: scale(1.04);
          }

          .about-slider__content {
            padding-left: 20px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
          }

          .about-slider__tag {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #5b9655;
            background: #eef6ed;
            padding: 6px 16px;
            border-radius: 20px;
            margin-bottom: 16px;
            width: fit-content;
          }

          .about-slider__title {
            font-size: 32px;
            font-weight: 700;
            color: #1a251e;
            line-height: 1.25;
            margin-bottom: 12px;
          }

          .about-slider__subtitle {
            font-size: 18px;
            font-weight: 600;
            color: #4a6350;
            margin-bottom: 16px;
          }

          .about-slider__desc {
            font-size: 15px;
            line-height: 1.7;
            color: #555e56;
            margin-bottom: 28px;
          }

          .about-slider__action-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 16px;
            margin-top: auto;
          }

          .about-slider__controls {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .about-slider__arrow-btn {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: #f0f4ef;
            border: 1px solid #d9e2d7;
            color: #273b2b;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.25s ease;
            font-size: 15px;
          }

          .about-slider__arrow-btn:hover {
            background: #5b9655;
            border-color: #5b9655;
            color: #ffffff;
            transform: scale(1.08);
          }

          .about-slider__pagination {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-left: 8px;
          }

          .about-slider__dot {
            width: 10px;
            height: 10px;
            border-radius: 5px;
            background: #cfdcd0;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
            padding: 0;
          }

          .about-slider__dot.active {
            width: 28px;
            background: #5b9655;
          }

          /* Fade in animation */
          .fade-in-slide {
            animation: fadeInSlide 0.45s ease forwards;
          }

          @keyframes fadeInSlide {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Scroll entrance animations */
          .about-slider-section .about-slider__nav-tabs,
          .about-slider-section .about-slider__card {
            opacity: 0;
            transform: translateY(35px);
            transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                        transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
            will-change: opacity, transform;
          }

          .about-slider-section.about-active .about-slider__nav-tabs,
          .about-slider-section.about-active .about-slider__card {
            opacity: 1;
            transform: translateY(0);
          }

          .about-slider-section.about-active .about-slider__card {
            transition-delay: 0.15s;
          }

          @media (max-width: 991px) {
            .about-slider__card {
              padding: 24px;
            }
            .about-slider__content {
              padding-left: 0;
              margin-top: 24px;
            }
            .about-slider__img-wrap {
              height: 280px;
            }
            .about-slider__title {
              font-size: 26px;
            }
          }
        `}
      </style>

      <section
        className={`about-slider-section ${isVisible ? 'about-active' : ''}`}
        id="about"
        ref={sectionRef}
        style={{ backgroundImage: `url(${getBgImageUrl()})` }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="container">
          {/* Quick Filter / Tabs Header */}
          <div className="about-slider__nav-tabs">
            {slides.map((slide, idx) => (
              <button
                key={slide._id || idx}
                className={`about-slider__tab-btn ${idx === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(idx)}
                type="button"
              >
                <i className={slide.tagIcon || 'fas fa-sparkles'}></i>
                <span>{idx + 1}. {slide.tag}</span>
              </button>
            ))}
          </div>

          {/* Main Slide Card */}
          <div className="about-slider__card">
            <div className="row align-items-center w-100 m-0">
              {/* Left Column: Visual / Image */}
              <div className="col-lg-5 p-0">
                <div key={`img-${currentSlide}`} className="about-slider__img-wrap fade-in-slide">
                  <img
                    src={getSlideImageUrl(active.image)}
                    alt={active.alt || active.title || 'PsychMitra Showcase'}
                  />
                </div>
              </div>

              {/* Right Column: Sliding Content */}
              <div className="col-lg-7 p-0">
                <div key={`content-${currentSlide}`} className="about-slider__content fade-in-slide">
                  <div className="about-slider__tag">
                    <i className={active.tagIcon || 'fas fa-heart'}></i>
                    <span>{active.tag}</span>
                  </div>

                  <h2 className="about-slider__title">{active.title}</h2>
                  {active.subtitle && (
                    <h4 className="about-slider__subtitle">{active.subtitle}</h4>
                  )}
                  <p className="about-slider__desc">{active.description}</p>

                  <div className="about-slider__action-row">
                    <a href={active.btnLink || '/services'} className="thm-btn">
                      {active.btnText || 'Explore'}
                    </a>

                    <div className="about-slider__controls">
                      <button
                        className="about-slider__arrow-btn"
                        onClick={handlePrev}
                        aria-label="Previous Slide"
                        type="button"
                      >
                        <i className="fas fa-arrow-left"></i>
                      </button>

                      <div className="about-slider__pagination">
                        {slides.map((_, idx) => (
                          <button
                            key={idx}
                            className={`about-slider__dot ${idx === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(idx)}
                            aria-label={`Slide ${idx + 1}`}
                            type="button"
                          />
                        ))}
                      </div>

                      <button
                        className="about-slider__arrow-btn"
                        onClick={handleNext}
                        aria-label="Next Slide"
                        type="button"
                      >
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default AboutSliderSection;
